import { ajax } from 'rxjs/ajax'
import { of } from 'rxjs'

import {
  containsValues,
  mergeToNewObject,
  toUrlQueryParams
} from '../util/object.helpers'
import { APP_CONFIG } from '../constants/appConfig'

const USERS = '/users'
const TOKENS = '/tokens'
const TRANSACTIONS = '/transactions'
const BRANDS = '/brands'
const NFTS = '/nfts'
const TOKEN_EXCHANGE_WHITELIST = '/token-exchange-whitelist'
const API_KEYS = '/api-keys'
const ACCESS_CONTROL = '/access-control'
const CHECKOUT_SESSION = '/checkout-session'
const BILLING_PORTAL = '/billing-portal'
const SUBSCRIPTIONS = '/subscriptions'
const STRIPE = '/stripe'
const ANALYTICS = '/analytics'
const DOWNLOAD_REPORT = '/csv-email'
const PREFETCH = '/prefetch'
const CANNY_SSO = '/canny-sso/sso-token'
const CLAIMS = '/claims'
const SURVEYS = '/surveys'
const USER_SURVEYS = '/user-surveys'

const DEFAULT_HEADERS = {
  'content-type': 'application/json'
}

const CONTENT_TYPE_FORM_DATA_HEADERS = {
  'content-type': 'multipart/form-data'
}

class ApiService {
  constructor() {
    this.usersUrl = APP_CONFIG.API_URL + USERS
    this.brandsUrl = APP_CONFIG.API_URL + BRANDS
    this.nftUrl = APP_CONFIG.API_URL + NFTS
    this.paymentGatewayUrl = APP_CONFIG.SLS_URL + STRIPE
    this.analyticsUrl = APP_CONFIG.SLS_URL + ANALYTICS
    this.downloadReportEmail = APP_CONFIG.SLS_URL + DOWNLOAD_REPORT
    this.prefetchAPI = APP_CONFIG.API_URL + PREFETCH
    this.requestFeatureSSOAPI = APP_CONFIG.SLS_URL + CANNY_SSO
  }

  // USERS
  getUserByClientId = (id, accessToken) =>
    ajax.get(this.usersUrl + formatToParam(id), getHeaders({ accessToken }))

  createUser = (userData, accessToken, recaptchaToken) =>
    ajax.post(
      this.usersUrl,
      userData,
      getHeaders({ accessToken, recaptchaToken })
    )

  updateUser = (id, updatedUserData, accessToken) => {
    return ajax.patch(
      this.usersUrl + formatToParam(id),
      { dashboard_user: updatedUserData },
      getHeaders({ accessToken })
    )
  }

  // BRANDS
  getBrands = (accessToken) => {
    return ajax.get(this.brandsUrl, getHeaders({ accessToken }))
  }

  createBrand = (data, accessToken) => {
    return ajax.post(this.brandsUrl, data, getHeaders({ accessToken }))
  }

  updateBrand = ({ id, data }, accessToken) => {
    return ajax.patch(brandsPath(id), data, getHeaders({ accessToken }))
  }

  // TOKENS
  getTokens = (brandId, accessToken, params) => {
    return ajax.get(
      buildTokensUrl(brandId) + buildTypesQueryParams(params, true),
      getHeaders({ accessToken })
    )
  }

  getTokenById = (brandId, id, accessToken) => {
    return ajax.get(
      buildTokensUrl(brandId) + formatToParam(id),
      getHeaders({ accessToken })
    )
  }

  createToken = (brandId, data, accessToken) => {
    return ajax.post(buildTokensUrl(brandId), data, getHeaders({ accessToken }))
  }

  updateToken = (brandId, tokenId, data, accessToken) => {
    return ajax.patch(
      buildTokensUrl(brandId) + formatToParam(tokenId),
      data,
      getHeaders({ accessToken })
    )
  }

  getTokenStats = ({ tokenId, ...restAttr }, accessToken) => {
    return ajax.get(
      `${getAnalyticsPath(
        'transactions-overview',
        tokenId
      )}${buildTypesQueryParams(restAttr, true)}`,
      getHeaders({ accessToken })
    )
  }

  getNftUsers = (brandId, tokenId, accessToken, params) => {
    return ajax.get(
      brandsPath(brandId) +
        NFTS +
        formatToParam(tokenId) +
        USERS +
        `?${toUrlQueryParams(params)}`,
      getHeaders({ accessToken })
    )
  }

  redeemNftExclusiveContent = (brandId, tokenId, data, accessToken) => {
    return ajax.post(
      brandsPath(brandId) + TOKENS + formatToParam(tokenId) + CLAIMS,
      data,
      getHeaders({ accessToken })
    )
  }

  transferNft = (brandId, data, accessToken) => {
    return ajax.post(
      getBrandTransactionsPath(brandId),
      data,
      getHeaders({ accessToken })
    )
  }

  // TRANSACTIONS
  getTransactions = (
    { brandId, tokenId, exclude_token_to_types, ...restAttr },
    accessToken
  ) => {
    // @TODO: passing token_id query param need to recheck once we have multiple tokens handler in the system.
    // token_id=${tokenId}&
    console.log(tokenId)
    return ajax.get(
      `${getBrandTransactionsPath(brandId)}${buildTypesQueryParams(
        restAttr,
        true
      )}${formatListToQuery(exclude_token_to_types, 'exclude_token_to_types')}`,
      getHeaders({ accessToken })
    )
  }

  getTransactionsChart = ({ tokenId, ...restAttr }, accessToken) => {
    return ajax.get(
      `${getAnalyticsPath(
        'transactions-graph',
        tokenId
      )}${buildTypesQueryParams(restAttr, true)}`,
      getHeaders({ accessToken })
    )
  }

  // MEMBERS
  getMembers = (params, brandId, accessToken) => {
    return ajax.get(
      `${getBrandUsersPath(brandId)}?${toUrlQueryParams(params)}`,
      getHeaders({ accessToken })
    )
  }

  getMemberDetails = (userId, brandId, accessToken) => {
    return ajax.get(
      `${getBrandUsersPath(brandId)}${formatToParam(userId)}`,
      getHeaders({ accessToken })
    )
  }

  getMemberBalances = (brandId, authId, accessToken) => {
    return ajax.get(
      `${getBrandUsersPath(brandId)}${formatToParam(authId)}${formatToParam(
        'balances'
      )}`,
      getHeaders({ accessToken })
    )
  }

  memberTransaction = (brandId, data, accessToken) => {
    return ajax.post(
      getBrandTransactionsPath(brandId),
      data,
      getHeaders({ accessToken })
    )
  }

  // TEAM MEMBERS
  getTeamMembers = (brandId, accessToken) => {
    return ajax.get(
      getBrandAccessControlPath(brandId),
      getHeaders({ accessToken })
    )
  }

  addTeamMember = (brandId, data, accessToken) => {
    return ajax.post(
      getBrandAccessControlPath(brandId),
      data,
      getHeaders({ accessToken })
    )
  }

  removeTeamMember = (brandId, accessControlId, accessToken) => {
    return ajax.delete(
      getBrandAccessControlPath(brandId) + formatToParam(accessControlId),
      getHeaders({ accessToken })
    )
  }

  // FILE UPLOAD
  getFileUploadPreSignedUrl = (fileExtension, accessToken) => {
    return ajax.get(
      APP_CONFIG.API_URL +
        formatToParam(
          `file-uploads/presigned-url?file_extension=${fileExtension}`
        ),
      getHeaders({ accessToken })
    )
  }

  uploadFile = (url, fileContent) => {
    return ajax.put(url, fileContent, CONTENT_TYPE_FORM_DATA_HEADERS)
  }

  // EXCHANGE
  saveExchangeSettings = (data, accessToken) => {
    console.log(accessToken)
    return of({ response: { data } })
    // return ajax.patch(
    //   this.brandsUrl,
    //   data,
    //   getHeaders({ accessToken })
    // )
  }

  getPotentialPartners = ({ countries, ...restParams }, accessToken) => {
    return ajax.get(
      `${APP_CONFIG.API_URL}/potential-partners${buildTypesQueryParams(
        restParams,
        true
      )}${formatListToQuery(countries, 'countries')}`,
      getHeaders({ accessToken })
    )
  }

  getExchangeWhitelist = (
    brandId,
    { countries, ...restParams },
    accessToken
  ) => {
    return ajax.get(
      `${brandsPath(brandId)}${TOKEN_EXCHANGE_WHITELIST}${buildTypesQueryParams(
        restParams,
        true
      )}${formatListToQuery(countries, 'countries')}`,
      getHeaders({ accessToken })
    )
  }

  createExchangeConnection = (brandId, data, accessToken) => {
    return ajax.post(
      `${brandsPath(brandId)}${TOKEN_EXCHANGE_WHITELIST}`,
      data,
      getHeaders({ accessToken })
    )
  }

  updateExchangeConnection = (brandId, tokenWhitelistId, data, accessToken) => {
    return ajax.patch(
      `${brandsPath(brandId)}${TOKEN_EXCHANGE_WHITELIST}${formatToParam(
        tokenWhitelistId
      )}`,
      data,
      getHeaders({ accessToken })
    )
  }

  // API KEYS
  getAPIKeys = (brandId, accessToken) => {
    return ajax.get(buildAPIKeysUrl(brandId), getHeaders({ accessToken }))
  }

  createAPIKey = (brandId, data, accessToken) => {
    return ajax.post(
      buildAPIKeysUrl(brandId),
      data,
      getHeaders({ accessToken })
    )
  }

  deleteAPIKey = (brandId, apiKeyId, accessToken) => {
    return ajax.delete(
      buildAPIKeysUrl(brandId) + formatToParam(apiKeyId),
      getHeaders({ accessToken })
    )
  }

  // API CONFIGS
  getDashboardConfigs = (accessToken) => {
    return ajax.get(this.prefetchAPI, getHeaders({ accessToken }))
  }

  // CANNY SSO
  getCannySSOToken = (data, accessToken) => {
    return ajax.post(
      this.requestFeatureSSOAPI,
      data,
      getHeaders({ accessToken })
    )
  }

  // DOWNLOAD REPORT TO EMAIL
  downloadReportToEmail = (reportType, data, accessToken) => {
    return ajax.post(
      `${this.downloadReportEmail}${formatToParam(reportType)}`,
      data,
      getHeaders({ accessToken })
    )
  }

  // BILLING
  generateCheckoutSession = (tokenId, params, accessToken) => {
    return ajax.get(
      `${this.paymentGatewayUrl}${CHECKOUT_SESSION}${formatToParam(
        tokenId
      )}?${toUrlQueryParams(params)}`,
      getHeaders({ accessToken })
    )
  }

  getPackageSubscriptions = (tokenId, accessToken) => {
    return ajax.get(
      `${this.paymentGatewayUrl}${SUBSCRIPTIONS}${formatToParam(tokenId)}`,
      getHeaders({ accessToken })
    )
  }

  updatePackageSubscription = (tokenId, subscriptionId, data, accessToken) => {
    return ajax.patch(
      `${this.paymentGatewayUrl}${SUBSCRIPTIONS}${formatToParam(
        tokenId
      )}${formatToParam(subscriptionId)}`,
      data,
      getHeaders({ accessToken })
    )
  }

  generateViewBillingSession = (tokenId, accessToken) => {
    return ajax.get(
      `${this.paymentGatewayUrl}${BILLING_PORTAL}${formatToParam(tokenId)}`,
      getHeaders({ accessToken })
    )
  }

  // ANALYTICS
  getAnalyticsData = (path, tokenId, params, accessToken) => {
    return ajax.get(
      `${getAnalyticsPath(path, tokenId)}${buildTypesQueryParams(
        params,
        true
      )}`,
      getHeaders({ accessToken })
    )
  }

  // SURVEYS
  getSurveys = (accessToken) => {
    return ajax.get(surveysPath(), getHeaders({ accessToken }))
  }

  submitUserSurvey = (data, accessToken) => {
    return ajax.post(userSurveysPath(), data, getHeaders({ accessToken }))
  }

  updateUserSurvey = (userSurveyId, data, accessToken) => {
    return ajax.patch(
      userSurveysPath() + formatToParam(userSurveyId),
      data,
      getHeaders({ accessToken })
    )
  }

  getUserSurveys = (accessToken) => {
    return ajax.get(userSurveysPath(), getHeaders({ accessToken }))
  }

  getUserSurveyAnalytics = (accessToken) => {
    return ajax.get(
      getAnalyticsPath('lad-user-survey-responses'),
      getHeaders({ accessToken })
    )
  }
}

const apiService = new ApiService()

export default apiService

const getHeaders = (data) => {
  const { accessToken, recaptchaToken } = data || {}
  const headers = {}
  if (accessToken) {
    headers.Authorization = 'Bearer ' + accessToken
  }
  if (recaptchaToken) {
    headers.recaptcha_token = recaptchaToken
  }
  return mergeToNewObject(DEFAULT_HEADERS, headers)
}

const formatToParam = (value) => {
  return value ? `/${value}` : ''
}

const buildTokensUrl = (brandId) => {
  return brandsPath(brandId) + TOKENS
}

const buildAPIKeysUrl = (brandId) => {
  return brandsPath(brandId) + API_KEYS
}

export const formatToQuery = (label, value) => {
  return `?${label}=${value}`
}

export const formatListToQuery = (
  list,
  paramName = 'types',
  startDelimiter = '&'
) => {
  if (!list) {
    return ''
  }
  let url = ''
  if (list.length > 0) {
    url += startDelimiter
    const separator = '&'
    const listLastIndex = list.length - 1
    list.forEach((type, index) => {
      url += `${paramName}[]=${type}${listLastIndex !== index ? separator : ''}`
    })
  }
  return url
}

const buildTypesQueryParams = (params, isPositionStarting = false) => {
  if (containsValues(params)) {
    const { types, ...restAttr } = params
    return `${isPositionStarting ? '?' : '&'}${toUrlQueryParams(
      restAttr
    )}${formatListToQuery(types)}`
  }
  return ''
}

const brandsPath = (brandId) => {
  return APP_CONFIG.API_URL + BRANDS + formatToParam(brandId)
}

const getBrandTransactionsPath = (brandId) => {
  return brandsPath(brandId) + TRANSACTIONS
}

const getBrandUsersPath = (brandId) => {
  return brandsPath(brandId) + USERS
}

const getBrandAccessControlPath = (brandId) => {
  return brandsPath(brandId) + ACCESS_CONTROL
}

const getAnalyticsPath = (path, tokenId = null) => {
  return `${APP_CONFIG.SLS_URL}${ANALYTICS}/${path}` + formatToParam(tokenId)
}

const userSurveysPath = () => {
  return APP_CONFIG.API_URL + USER_SURVEYS
}

const surveysPath = () => {
  return APP_CONFIG.API_URL + SURVEYS
}
